/**
 * Fintrack shared rates — ONE source of truth.
 *
 * Stamp duty and income tax were hardcoded in The Gap, here in the CRM, and again in the
 * Platform. Three copies is three chances to be a year out of date, and on 16 Sep 2026 all
 * three were: they still carried the 16% bracket that fell to 15% on 1 July 2026, so every
 * discovery call and strategy session was quoting a client the wrong tax.
 *
 * Everything now comes from https://fintrack.com.au/rates.json. The copy below is only a
 * fallback so the app still works if the fetch fails; whichever carries the later version
 * wins. Update the published file and every product picks it up without a deploy.
 *
 * DO NOT hardcode a tax bracket or a duty scale anywhere else in this repo.
 */

export const RATES_URL = "https://fintrack.com.au/rates.json";

export interface Bracket { from: number; base?: number; rate?: number; flatOnTotal?: number; }
export interface Rates {
    version: string;
    effectiveFrom: string;
    reviewBy: string;
    incomeTax: { year: string; brackets: Bracket[]; medicare: { rate: number; singleThreshold: number } };
    stampDuty: Record<string, any>;
    otherBuyingCosts: { conveyancing: number; buildingAndPest: number; lenderFees: number };
}

/** Last known good. Kept deliberately small — the full duty tables come from the file. */
const FALLBACK: Rates = {
    version: "2026.09.16b",
    effectiveFrom: "2026-07-01",
    reviewBy: "2027-06-15",
    incomeTax: {
        year: "2026-27",
        brackets: [
            { from: 0, base: 0, rate: 0 },
            { from: 18200, base: 0, rate: 0.15 },
            { from: 45000, base: 4020, rate: 0.30 },
            { from: 135000, base: 31020, rate: 0.37 },
            { from: 190000, base: 51370, rate: 0.45 },
        ],
        medicare: { rate: 0.02, singleThreshold: 28011 },
    },
    stampDuty: {},
    otherBuyingCosts: { conveyancing: 1800, buildingAndPest: 650, lenderFees: 700 },
};

let current: Rates = FALLBACK;
let loading: Promise<Rates> | null = null;

/** Fetched once per page load, then cached. Never throws — a stale number beats a crash. */
export function loadRates(): Promise<Rates> {
    if (loading) return loading;
    loading = fetch(`${RATES_URL}?${Date.now()}`)
        .then(r => (r.ok ? r.json() : null))
        .then((live: Rates | null) => {
            if (live && live.version && live.version >= current.version) current = live;
            return current;
        })
        .catch(() => current);
    return loading;
}

export function getRates(): Rates { return current; }

/** Income tax, excluding the Medicare levy — matches the old calculateAustralianTax(). */
export function australianTax(taxableIncome: number): number {
    const t = getRates().incomeTax;
    let duty = 0;
    for (const b of t.brackets) {
        if (taxableIncome > b.from) duty = (b.base ?? 0) + (taxableIncome - b.from) * (b.rate ?? 0);
    }
    return duty;
}

/** Income tax including the Medicare levy. */
export function australianTaxWithMedicare(taxableIncome: number): number {
    const t = getRates().incomeTax;
    const levy = taxableIncome > t.medicare.singleThreshold ? taxableIncome * t.medicare.rate : 0;
    return australianTax(taxableIncome) + levy;
}

/** Residential transfer duty for an investor, by state. Returns 0 if the state is unknown. */
export function stampDuty(state: string, value: number, firstHome: "no" | "est" | "new" = "no"): number {
    const d = getRates().stampDuty?.[state];
    if (!d || value <= 0) return 0;

    let duty: number;
    if (d.formula === "quadratic" && value <= d.quadratic.upTo) {
        const v = value / 1000;
        duty = d.quadratic.a * v * v + d.quadratic.b * v;
    } else {
        const b = (d.brackets as Bracket[]).filter(x => value >= x.from).pop()!;
        duty = b.flatOnTotal != null ? value * b.flatOnTotal : (b.base ?? 0) + (value - b.from) * (b.rate ?? 0);
    }
    if (d.minDuty) duty = Math.max(duty, d.minDuty);

    const f = d.fhb || {};
    if (firstHome !== "no") {
        const eligible = firstHome === "new" || (firstHome === "est" && f.coversEstablished !== false);
        if (eligible) {
            if (f.type === "full") return 0;
            if (f.type === "newOnly") return firstHome === "new" ? 0 : duty;
            if (f.type === "taper") {
                if (value <= f.exemptTo) return 0;
                if (value < f.concessionTo) {
                    // NSW scales the duty at the TOP of the range; VIC scales the duty on the
                    // price paid. Neither publishes a formula; both verified against their own
                    // calculators.
                    const basis = f.taperOn === "top" ? stampDuty(state, f.concessionTo, "no") : duty;
                    return Math.max(0, (basis * (value - f.exemptTo)) / (f.concessionTo - f.exemptTo));
                }
            }
            if (f.type === "cliff") {
                if (value <= f.exemptTo) return 0;
                if (value < f.concessionTo) return (value - f.exemptTo) * f.concessionRate;
            }
        }
    }
    return duty;
}
